/**
 * 鱼塘租赁枚举（与后端 FishPondLeaseEnums / EnterpriseType 对齐，值为数字）
 *
 * 枚举常量值用于代码比较（如 `ContractStatus.Signed === 3`），不可变。
 * 枚举标签/Options 从后端 GET /api/FishPondLeaseEnum/all 动态获取，前端不再硬编码。
 * 修改枚举只需修改后端，前端无需改动。
 *
 * 模块加载时自动发起请求，首次请求完成后填充 Options 数组。
 * 组件直接 import Options 数组即可使用，无需额外初始化。
 */
import Vue from "vue";
import api from "./api";

const ENUM_API = "/api/FishPondLeaseEnum";

// ========== 枚举常量值（仅数值，用于代码比较） ==========

/** 企业类型：1普通 / 2出租二级(农发) / 3出租三级(子公司) / 4承租方 */
export const EnterpriseType = {
  Normal: 1,
  LessorLevel2: 2,
  LessorLevel3: 3,
  Lessee: 4
};

/**
 * 源系统按公司层级过滤菜单（hybridControl companyLevel）：
 * - level=2：审核组（标的物/承租方审核）+ 合同/租金查看 + 鱼塘汇总 + 标的物配置 + 合同模板管理
 * - level=3：管理组（标的物/承租方管理）+ 合同/租金 + 统计；无审核/模板/配置
 * - 其他：不展示鱼塘租赁业务菜单
 */
export const LEASE_MENU_PERMISSION_IDS_BY_ENTERPRISE_TYPE = {
  [EnterpriseType.LessorLevel2]: [521, 523, 524, 525, 526, 527, 528],
  [EnterpriseType.LessorLevel3]: [520, 522, 524, 525, 527]
};

/** 按企业类型判断某租赁权限菜单是否可见（仍需配合角色权限 show） */
export function isLeaseMenuVisibleByEnterpriseType(enterpriseType, permissionId) {
  const allowed = LEASE_MENU_PERMISSION_IDS_BY_ENTERPRISE_TYPE[Number(enterpriseType)];
  if (!allowed) return false;
  return allowed.indexOf(Number(permissionId)) >= 0;
}

// ========== 枚举常量值（用于代码比较） ==========

export const PropertyType = { FishPond: 1, Land: 2 };
export const PropertySubType = { FreshWater: 1, SeaWater: 2 };
export const PropertyStatus = { Idle: 1, Rented: 2, Disabled: 3 };
export const LeaseAuditStatus = {
  PendingSubmit: 1,
  Pending: 2,
  Approved: 3,
  Rejected: 4
};
export const TenantType = { Enterprise: 1, SelfEmployed: 2, Individual: 3 };
export const TenantStatus = { Normal: 1, Disabled: 2 };
export const ContractStatus = {
  New: 1,
  PendingSign: 2,
  Signed: 3,
  Performing: 4,
  Completed: 5,
  Terminated: 6,
  PendingAudit: 7
};
export const PaymentStatus = { None: 1, PendingPay: 2, Paid: 3, Overdue: 4 };
export const PayMethod = { Offline: 1, Online: 2 };
export const SignMethod = { Offline: 1, Online: 2 };
export const AssetType = {
  WestWithAsset: 1,
  EastWithAsset: 2,
  NoAsset: 3,
  FourByFourHundred: 4
};
export const ContractType = { New: 1, Renew: 2 };
export const FeeItemType = { Rent: 1, ManageFee: 2, Deposit: 3 };
export const AuditBusType = { Property: 1, Tenant: 2 };
export const AuditActionType = { Submit: 1, Approve: 2, Reject: 3 };
export const WidgetType = { Text: 1, Number: 2, Select: 3, Date: 4 };

// ========== Options 数组（供下拉选择/表格展示，从后端动态填充） ==========

export const PropertyTypeOptions = [];
export const PropertySubTypeOptions = [];
export const PropertyStatusOptions = [];
export const LeaseAuditStatusOptions = [];
export const TenantTypeOptions = [];
export const TenantStatusOptions = [];
export const ContractStatusOptions = [];
export const PaymentStatusOptions = [];
export const PayMethodOptions = [];
export const SignMethodOptions = [];
export const AssetTypeOptions = [];
export const ContractTypeOptions = [];
export const FeeItemTypeOptions = [];

// Options 数组映射表（枚举类型名 → Options 数组引用）
const OPTIONS_MAP = {
  PropertyType: PropertyTypeOptions,
  PropertySubType: PropertySubTypeOptions,
  PropertyStatus: PropertyStatusOptions,
  LeaseAuditStatus: LeaseAuditStatusOptions,
  TenantType: TenantTypeOptions,
  TenantStatus: TenantStatusOptions,
  ContractStatus: ContractStatusOptions,
  PaymentStatus: PaymentStatusOptions,
  PayMethod: PayMethodOptions,
  SignMethod: SignMethodOptions,
  AssetType: AssetTypeOptions,
  ContractType: ContractTypeOptions,
  FeeItemType: FeeItemTypeOptions
};

// 枚举类型名 → 枚举项列表缓存（{ value, name, label }[]）
let _enumItemsCache = {};

/**
 * 前端历史写法（camelCase / 简称）→ 后端 enumName（PascalCase）
 * 如 auditStatus → LeaseAuditStatus
 */
const ENUM_NAME_ALIASES = {
  auditstatus: "LeaseAuditStatus",
  leaseauditstatus: "LeaseAuditStatus",
  tenanttype: "TenantType",
  tenantstatus: "TenantStatus",
  propertytype: "PropertyType",
  propertysubtype: "PropertySubType",
  propertystatus: "PropertyStatus",
  contractstatus: "ContractStatus",
  paymentstatus: "PaymentStatus",
  paymethod: "PayMethod",
  signmethod: "SignMethod",
  assettype: "AssetType",
  contracttype: "ContractType",
  feeitemtype: "FeeItemType"
};

/** 枚举加载版本：模板里调用 enumLabel 时依赖它，加载完成后自动刷新展示 */
export const enumLoadState = Vue.observable({ version: 0, ready: false });

function resolveEnumName(enumName) {
  if (!enumName) return "";
  if (_enumItemsCache[enumName]) return enumName;

  const lower = String(enumName).toLowerCase();
  const alias = ENUM_NAME_ALIASES[lower];
  if (alias && _enumItemsCache[alias]) return alias;
  if (alias) return alias;

  const keys = Object.keys(_enumItemsCache);
  const hit = keys.find(function(k) {
    return k.toLowerCase() === lower;
  });
  if (hit) return hit;

  const pascal = enumName.charAt(0).toUpperCase() + enumName.slice(1);
  if (_enumItemsCache[pascal]) return pascal;
  return pascal;
}

/**
 * 获取枚举标签
 * @param {string} enumName - 枚举类型名，如 "TenantType" / "tenantType"；审核状态可用 "LeaseAuditStatus" 或 "auditStatus"
 * @param {number|string} value - 枚举值
 * @returns {string} 中文标签；未找到时返回 value 字符串或 "-"
 */
export function enumLabel(enumName, value) {
  // 依赖响应式 version，枚举异步加载完成后表格/详情会自动重绘
  void enumLoadState.version;

  const resolved = resolveEnumName(enumName);
  const items = _enumItemsCache[resolved];
  if (items) {
    const hit = items.find(function(item) {
      return item.value === Number(value);
    });
    if (hit) return hit.label;
  }
  return value == null || value === "" ? "-" : String(value);
}

/**
 * 标签样式（对齐源系统 getTagType）
 * audit: APPROVED success / PENDING warning / REJECTED danger / PENDING_SUBMIT info
 * property: IDLE success / RENTED warning / DISABLED danger
 * tenant: NORMAL success / DISABLED danger
 * contract / payment: 同原系统常见状态色
 */
export function statusTagType(kind, value) {
  const v = Number(value);
  if (kind === "audit") {
    if (v === 3) return "success"; // Approved
    if (v === 2) return "warning"; // Pending
    if (v === 4) return "danger"; // Rejected
    return "info"; // PendingSubmit
  }
  if (kind === "property") {
    if (v === 1) return "success"; // Idle 空闲中
    if (v === 2) return "warning"; // Rented 使用中
    if (v === 3) return "danger"; // Disabled 已禁用
    return "info";
  }
  if (kind === "tenant") {
    if (v === 1) return "success"; // Normal
    if (v === 2) return "danger"; // Disabled
    return "info";
  }
  if (kind === "contract") {
    if (v === 3 || v === 4) return "success"; // Signed / Performing
    if (v === 2 || v === 7) return "warning"; // PendingSign / PendingAudit
    if (v === 6) return "danger"; // Terminated
    if (v === 5 || v === 1) return "info"; // Completed / New
    return "info";
  }
  if (kind === "payment") {
    if (v === 3) return "success"; // Paid 已结清
    if (v === 2) return "warning"; // PendingPay
    if (v === 4) return "danger"; // Overdue
    return "info"; // None
  }
  return "info";
}

// ========== 自动初始化（模块加载时从后端获取） ==========

let _loadPromise = null;

async function fetchAndPopulateEnums() {
  try {
    const res = await api.get(`${ENUM_API}/all`);
    const groups = (res && res.data) || [];
    groups.forEach(function(group) {
      const enumName = group.enumName;
      const items = (group.items || []).map(function(item) {
        return {
          value: item.value,
          label: item.label
        };
      });

      // 缓存到 items 映射（供 enumLabel 使用）
      _enumItemsCache[enumName] = items;

      // 填充对应的 Options 数组（下拉选项）
      const target = OPTIONS_MAP[enumName];
      if (target) {
        // 合同状态搜索下拉对齐源系统：展示 待签约(2)/已签约(3)/履约中(4)/已完成(5)/已终止(6)
        var optionItems = items;
        if (enumName === "ContractStatus") {
          optionItems = items.filter(function(it) {
            var v = Number(it.value);
            return v === 2 || v === 3 || v === 4 || v === 5 || v === 6;
          });
        }
        target.splice(0, target.length, ...optionItems);
      }
    });
    enumLoadState.ready = true;
    enumLoadState.version += 1;
  } catch (e) {
    // 接口失败时静默处理，enumLabel 会降级返回原始值
    enumLoadState.ready = false;
  }
}

/** 等待枚举加载完成（页面可在拉列表前 await，避免首屏闪数字） */
export function ensureEnumsLoaded() {
  if (!_loadPromise) {
    _loadPromise = fetchAndPopulateEnums();
  }
  return _loadPromise;
}

// 模块加载时自动发起请求
ensureEnumsLoaded();
